<template>
  <div :class="rootClasses" :style="rootStyles">
    <ElementControls
      v-if="editing"
      :no-page-break="!pageBreakAllowed"
      @delete="$emit('delete')"
      @duplicate="$emit('duplicate')"
      @page-break="$emit('page-break')"
      @hide="$emit('hide')"
    />
    <div :class="layoutClasses">
      <div
        v-if="label || description"
        class="formester-label-description-wrapper"
        :style="labelDescriptionWrapperStyles"
      >
        <label
          :class="labelClasses"
          :style="labelStyles"
          :for="item.label + item.id"
          v-html="label"
        />
        <SmartText
          :active="active"
          :editing="editing"
          :item="item"
          :style="descriptionStyles"
          :text="description"
        />
      </div>

      <div class="image-block-wrapper" :style="columns">
        <template v-if="multipleSelect">
          <div
            v-for="option in shuffledOptions"
            :key="option.id"
            class="image-block pointer"
            :class="{
              'alternate-image-block': item.isAlternateStyleEnabled,
              selected: isChecked(option),
              'correct-option':
                revealAnswer && correctAnswers.includes(option.id),
              'wrong-option':
                revealAnswer && !correctAnswers.includes(option.id),
            }"
            @click="toggleCheck(option)"
          >
            <div class="upload-block">
              <img
                v-if="option.image?.image"
                :alt="option.label"
                :src="option.image.image"
              />
              <ImageIcon
                v-else
                class="placeholder-image"
                height="30"
                width="30"
                color="#98A2B3"
              />
            </div>
            <div class="label-wrappper">
              <input
                :id="option.id"
                type="checkbox"
                tabindex="0"
                :checked="isChecked(option)"
                class="custom-checkbox formester-input"
                :name="item.label"
                :required="required"
                :disabled="isEditingDisabled || editing || isDisabled"
              />
              <div v-if="showLabel" class="custom-checkmark"></div>
              <span v-if="showLabel" class="option-label" :style="optionStyles">
                {{ option.label }}
              </span>
            </div>
          </div>
        </template>
        <template v-else>
          <div
            v-for="option in shuffledOptions"
            :key="option.label"
            class="image-block"
            :class="{
              'alternate-image-block': item.isAlternateStyleEnabled,
              selected: isChecked(option),
              'correct-option':
                revealAnswer && correctAnswers.includes(option.id),
              'wrong-option':
                revealAnswer && !correctAnswers.includes(option.id),
            }"
            :style="propertiesStyles"
            @keydown="chooseOption"
            @click="toggleCheck(option)"
          >
            <div class="upload-block">
              <img
                v-if="option.image?.image"
                :alt="option.label"
                :src="option.image.image"
              />
              <ImageIcon
                v-else
                class="placeholder-image"
                height="30"
                width="30"
                color="#98A2B3"
              />
            </div>
            <label class="label-wrappper mb-0">
              <input
                :id="`${item.label}-${item.id}`"
                :name="`${item.label}-${item.id}`"
                class="custom-radio formester-input"
                type="radio"
                tabindex="0"
                :checked="isChecked(option)"
                :required="required"
                :disabled="isEditingDisabled || editing || isDisabled"
                @click="toggleCheck(option)"
              />
              <div v-if="showLabel" class="custom-radio-button"></div>
              <span
                v-if="showLabel"
                :for="`${item.label}-${item.id}`"
                class="option-label"
                :style="optionStyles"
              >
                {{ option.label }}
              </span>
            </label>
          </div>
        </template>
      </div>

      <div
        v-if="showAnswerText"
        class="d-flex align-items-center justify-content-end mt-2"
        style="text-decoration: underline"
        :style="revealAnswerStyle"
      >
        <span class="pointer" @click="() => (revealAnswer = true)">
          Answer
        </span>
      </div>

      <div
        v-if="revealAnswer && !item.isAlternateStyleEnabled"
        :style="revealAnswerStyle"
      >
        <b>Correct answer:</b> {{ correctAnswerLabels.join(", ") }}
      </div>

      <ErrorMessage :item="item" />

      <QuizFeedback
        v-if="revealAnswer"
        class="mt-3"
        :type="quizFeedbackType"
        :message="quizFeedbackMessage"
      />
    </div>
  </div>
</template>

<script>
import ElementControls from "@/components/form_fields/shared/element-controls.vue";
import commonStyles from "@/components/form_fields/shared/commonStyles";
import sharedActions from "@/components/form_fields/shared/sharedActions";
import ErrorMessage from "@/components/form_fields/shared/error-message.vue";
import SmartText from "@/components/form_fields/shared/smart-text.vue";
import QuizFeedback from "@/components/form_fields/shared/_quiz-feedback.vue";
import ImageIcon from "@/components/icons/image.vue";

export default {
  name: "PictureChoice",
  components: {
    ElementControls,
    ErrorMessage,
    SmartText,
    QuizFeedback,
    ImageIcon,
  },
  mixins: [commonStyles, sharedActions],
  props: {
    item: {
      type: Object,
      required: true,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    active: {
      type: Boolean,
      default: false,
    },
    styling: {
      type: Object,
      required: true,
    },
    pageBreakAllowed: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["delete", "page-break", "duplicate", "hide", "update"],
  data() {
    return {
      uploadPercentage: 0,
      isUploading: false,
      arrowPressed: false,
    };
  },
  computed: {
    required() {
      if (!this.item.required) {
        return false;
      }

      return this.itemValue && this.itemValue.length === 0;
    },
    itemValue() {
      return this.item.value;
    },
    columns() {
      return { "grid-template-columns": `repeat(${this.item.columns}, 1fr)` };
    },
    multipleSelect() {
      return this.item.multipleSelect;
    },
    options() {
      return this.item.options;
    },
    multipleSelectCorrectAnswer() {
      return (
        this.itemValue.length > 0 &&
        this.itemValue.every((value) => this.correctAnswers.includes(value))
      );
    },
    singleSelectCorrectAnswer() {
      return this.correctAnswers.includes(this.itemValue[0]);
    },
    isCorrectAnswer() {
      return this.multipleSelect
        ? this.multipleSelectCorrectAnswer
        : this.singleSelectCorrectAnswer;
    },
    quizFeedbackType() {
      return this.isCorrectAnswer ? "success" : "danger";
    },
    quizFeedbackMessage() {
      if (this.isCorrectAnswer) {
        return this.item.correctAnswerFeedback || "Yup, Correct answer";
      }

      return this.item.wrongAnswerFeedback || "Opps! Wrong";
    },
    showLabel() {
      return !this.item.isAlternateStyleEnabled || this.item.showLabel;
    },
    propertiesStyles() {
      return {
        cursor: this.isDisabled && !this.editing ? "not-allowed" : "pointer",
        opacity: this.isDisabled && !this.editing ? 0.6 : 1,
      };
    },
  },
  watch: {
    multipleSelect: {
      immediate: true,
      handler() {
        this.adjustBlockHeight();
      },
    },
    columns: {
      immediate: true,
      handler() {
        this.adjustBlockHeight();
      },
    },
    options: {
      immediate: true,
      deep: true,
      handler() {
        this.adjustBlockHeight();
      },
    },
  },
  mounted() {
    this.adjustBlockHeight();
    window.addEventListener("resize", this.adjustBlockHeight);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.adjustBlockHeight);
  },
  methods: {
    isChecked(option) {
      const index = this.item.value.findIndex((opt) => opt === option.id);
      return index !== -1;
    },
    chooseOption(event) {
      if (this.styling.multiPage) {
        if (event.keyCode == 38 || event.keyCode == 40) {
          this.arrowPressed = true;
        }
      }
    },
    toggleCheck(option) {
      if (this.isEditingDisabled || this.editing || this.isDisabled) return;

      const multipleSelect = this.item.multipleSelect;

      if (!this.editing && !multipleSelect) {
        this.revealAnswer = true;
      }

      const updatedValue = [...this.itemValue];
      if (multipleSelect) {
        const index = updatedValue.findIndex((opt) => opt === option.id);
        if (index === -1) {
          updatedValue.push(option.id);
        } else {
          updatedValue.splice(index, 1);
        }
      } else {
        updatedValue[0] = option.id;
        if (!this.arrowPressed) {
          this.autoProgressToNext();
        }
      }
      this.$emit("update", updatedValue);
    },
    adjustBlockHeight() {
      this.$nextTick(() => {
        if (!this.$el) return;

        const blocks = this.$el.querySelectorAll(".image-block");
        blocks.forEach((block) => {
          const width = block.offsetWidth;
          const height = width / 1.5;
          block.children[0].style.height = `${height}px`;
        });
      });
    },
  },
};
</script>

<style scoped>
.image-block-wrapper {
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}
.image-block-wrapper.two-column {
  grid-template-columns: repeat(2, 1fr);
}
.image-block-wrapper .hidden-input-file {
  visibility: hidden;
  position: absolute;
}
.image-block {
  display: flex;
  flex-direction: column;
  cursor: pointer;
  box-sizing: border-box;
  gap: 8px;
}

.image-block.alternate-image-block {
  padding: 8px;
  background-color: var(--placeholder-color);
  border: 1px solid var(--border-color);
  border-radius: 4px;
}
.image-block.alternate-image-block.selected {
  outline: 3px solid var(--selected-option-color);
  outline-offset: -3px;
}
.image-block.correct-option {
  border-color: #38b000b2;
}
.image-block.wrong-option.selected {
  border-color: #d62828;
}

.image-block .label-wrappper {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  position: relative;
}
.image-block .upload-block {
  display: flex;
  align-items: center;
  justify-content: center;
}
.image-block .upload-block > img {
  object-fit: cover;
  max-width: 100%;
  max-height: 100%;
  border-radius: 4px;
}
.upload-block:has(.placeholder-image) {
  background-color: #f2f4f7;
  border-radius: 4px;
}

.custom-radio ~ .option-label,
.custom-checkbox ~ .option-label {
  cursor: pointer;
  user-select: none;
}

/* .image-block.selected .custom-radio ~ .option-label,
.image-block.selected .custom-checkbox ~ .option-label {
  color: var(--primary-color) !important;
} */

@media (max-width: 576px) {
  .image-block-wrapper {
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 12px;
  }
}
@media (max-width: 414px) {
  .image-block-wrapper {
    grid-template-columns: repeat(1, 1fr) !important;
    gap: 12px;
  }
}
</style>
